import { Element as SlateElement, Text as SlateText } from 'slate';
import type { PegaCustom } from 'slate';

import { disallowedURI } from '@pega/cosmos-react-core';
import type { ExcludeStrict } from '@pega/cosmos-react-core';

const EXCLUDED_ATTR_KEYS: ReadonlySet<string> = new Set([
  'type',
  'children',
  'isHeaderCell',
  'isHeaderRow'
]);

export const slateToHtmlMap = {
  link: 'a',
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'unordered-list': 'ul',
  'ordered-list': 'ol',
  'list-item': 'li',
  'block-quote': 'blockquote',
  // FIXME: Should render <pre><code>, but I don't know if this is really used anymore.
  'code-block': 'pre',
  'horizontal-rule': 'hr',
  table: 'table',
  'table-head': 'thead',
  'table-body': 'tbody',
  'table-row': 'tr',
  'table-cell': 'td',
  text: 'span',
  paragraph: 'p',
  bold: 'b',
  'line-through': 'del',
  italic: 'i',
  code: 'code',
  href: 'a',
  image: 'img'
};

// Reduces the properties in a slate node into the correct html attributes, mostly used for list-items at the moment
const getAttrs = (
  el: ExcludeStrict<SlateElement, PegaCustom.ImagePlaceholderElement | PegaCustom.CustomElement>
): [string, string][] => {
  // eslint-disable-next-line sonarjs/no-small-switch
  switch (el.type) {
    case 'list-item': {
      const attrs: [string, string][] = [];

      if (el.id) {
        attrs.push(['data-id', el.id]);
      }

      if (el.parentId) {
        attrs.push(['data-parent-id', el.parentId]);
      }

      if (el.order !== undefined) {
        attrs.push(['data-order', `${el.order}`]);
      }

      if (el.level !== undefined) {
        attrs.push(['data-level', `${el.level}`]);
      }

      return attrs;
    }

    default:
      return Object.entries(el).flatMap(([key, value]) => {
        const lowerKey = key.toLowerCase();

        if (
          EXCLUDED_ATTR_KEYS.has(key) ||
          // Strip event handlers.
          lowerKey.startsWith('on') ||
          value === undefined
        ) {
          return [];
        }

        if (lowerKey === 'href' && disallowedURI.test(value)) {
          return [];
        }

        return [[lowerKey, `${value}`]];
      });
  }
};

// Recursive function to reduce Slate Nodes into an HTML string
export const constructHtml = (nodes: (SlateElement | SlateText)[]): Node[] => {
  return nodes.reduce((acc: Node[], node) => {
    if (
      SlateElement.isElement(node) &&
      (node.type === 'image-placeholder' || node.type === 'custom' || node.type === 'override')
    )
      return acc;

    const isElement = SlateElement.isElement(node);

    if (SlateText.isText(node)) {
      const { text, ...restNode } = node;
      // Reducing inline style properties from a single Slate Node into the correct HTML tags
      const html = Object.keys(restNode).reduce((innerAcc: Element | null, key, i) => {
        const tag = slateToHtmlMap[key as 'bold' | 'line-through' | 'italic' | 'href'];

        if (!tag) return innerAcc;

        let innerEl: HTMLElement;

        if (tag === 'a') {
          const anchor = document.createElement(tag);
          if (restNode.href && !disallowedURI.test(restNode.href)) {
            anchor.setAttribute('href', restNode.href);
          }
          innerEl = anchor;
        } else {
          innerEl = document.createElement(tag);
        }

        if (i === 0) {
          innerEl.textContent = text;
          return innerEl;
        }

        if (innerAcc) {
          innerEl.appendChild(innerAcc);
        }

        return innerEl;
      }, null);

      if (html) {
        return acc.concat(html);
      }

      return acc.concat(document.createTextNode(text));
    }

    let htmlTag = isElement ? slateToHtmlMap[node.type] : undefined;
    if (isElement && node.type === 'table-cell' && node.isHeaderCell) {
      htmlTag = 'th';
    }

    if (!htmlTag) return acc;

    const el = document.createElement(htmlTag);

    for (const [attr, val] of getAttrs(node)) {
      el.setAttribute(attr, val);
    }

    for (const child of constructHtml(node.children)) {
      el.appendChild(child);
    }

    return acc.concat(el);
  }, []);
};

export const convertSlateToHtml = (nodes: (SlateElement | SlateText)[]): string => {
  if (
    nodes.length === 1 &&
    SlateElement.isElement(nodes[0]) &&
    SlateText.isText(nodes[0].children[0]) &&
    nodes[0].children[0].text === ''
  ) {
    return '';
  }

  const htmlNodes = constructHtml(nodes);
  const body = document.createElement('body');
  for (const node of htmlNodes) {
    body.appendChild(node);
  }

  return body.outerHTML;
};
